import React, { useState } from 'react'
import { Head, router } from '@inertiajs/react'

// PUBLIC patient-facing private-feedback form (/f/:token).
//
// Design rules, all load-bearing:
//  • Under a minute. One rating and ONE open question — IDENTICAL for every patient. Paul removed
//    the per-visit question (2026-07-31): a specific prompt anchors the answer and can crowd out
//    the thing that actually bothered them. The theme tagging reads whatever they write, so this
//    costs prompting, not measurement.
//  • The privacy line is ABOVE the fold and says who reads it BY NAME — that is the conversion lever
//    and the honesty guarantee at the same time.
//  • It states plainly that we know who they are. A form with no name field reads as anonymous, and a
//    patient who thinks they are anonymous, is candid, then gets phoned, never trusts us again.
//  • No clinical detail anywhere on the page — a forwarded link must disclose nothing.
//  • No review link, no offer, no booking CTA. That is what keeps this a service communication.

const FACES = [
  { v: 1, emoji: '😞', label: 'Poor' },
  { v: 2, emoji: '🙁', label: 'Not great' },
  { v: 3, emoji: '😐', label: 'Okay' },
  { v: 4, emoji: '🙂', label: 'Good' },
  { v: 5, emoji: '😃', label: 'Excellent' },
]

export default function PublicVisitFeedback({ token, context, practice, patient, visit, question, submitted }) {
  const [rating, setRating] = useState(null)
  const [comment, setComment] = useState('')
  const [contactOk, setContactOk] = useState(true)
  const [anonymous, setAnonymous] = useState(false)
  const [busy, setBusy] = useState(false)

  const isCancel = context === 'cancellation'

  const submit = (e) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    router.patch(`/f/${token}`,
      { rating, comment, contact_ok: contactOk, anonymous },
      { onFinish: () => setBusy(false), preserveScroll: true },
    )
  }

  if (submitted) {
    return (
      <Shell>
        <Head title="Thank you — Dr Chalita le Roux" />
        <div className="text-center py-10">
          <div className="text-5xl mb-4">🙏</div>
          <h1 className="text-2xl font-semibold text-slate-800">Thank you</h1>
          <p className="mt-3 text-slate-600 leading-relaxed">
            {practice?.manager || 'Our practice manager'} has your message. If you asked us to get back to you,
            we will be in touch.
          </p>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <Head title="Your feedback — Dr Chalita le Roux" />

      <h1 className="text-2xl font-semibold text-slate-800">
        {isCancel ? `Hi ${patient?.first_name}, sorry we missed you` : `Hi ${patient?.first_name}, how did we do?`}
      </h1>
      {visit?.date && (
        <p className="mt-1 text-sm text-slate-500">
          {isCancel ? 'Appointment on ' : 'Your visit on '}{visit.date}
        </p>
      )}

      {/* The promise, and the truth behind it — both above the fold, deliberately. */}
      <div className="mt-5 rounded-2xl bg-teal-50 border border-teal-200 p-4">
        <p className="text-sm text-teal-900 font-medium">
          🔒 This goes privately to {practice?.manager || 'our practice manager'}.
        </p>
        <p className="mt-1.5 text-sm text-teal-800 leading-relaxed">
          Reception and the clinical team do not see it. We can see it is from you, so we can follow up
          properly — you are not anonymous unless you tick the box at the end.
        </p>
        <p className="mt-1.5 text-xs text-teal-700 leading-relaxed">
          If you tell us about a health concern, we will pass that on to Dr Chalita so we can look after you.
        </p>
      </div>

      <form onSubmit={submit} className="mt-7 space-y-7">
        {!isCancel && (
          <fieldset>
            <legend className="text-base font-medium text-slate-800">How did you find your visit?</legend>
            <div className="mt-3 grid grid-cols-5 gap-2">
              {FACES.map((f) => (
                <button
                  key={f.v}
                  type="button"
                  onClick={() => setRating(f.v)}
                  aria-pressed={rating === f.v}
                  aria-label={f.label}
                  className={`rounded-2xl border py-3 transition ${
                    rating === f.v
                      ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/30'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className="block text-2xl leading-none">{f.emoji}</span>
                  <span className="mt-1.5 block text-[11px] text-slate-500">{f.label}</span>
                </button>
              ))}
            </div>
          </fieldset>
        )}

        <div>
          <label htmlFor="comment" className="block text-base font-medium text-slate-800">
            {question}
          </label>
          <textarea
            id="comment"
            rows={6}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-200 p-3 text-slate-800 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            placeholder="Big or small — the small things are usually the ones we never hear about"
          />
        </div>

        <div className="space-y-3 rounded-2xl bg-slate-50 p-4">
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <input type="checkbox" checked={contactOk} onChange={(e) => setContactOk(e.target.checked)}
                   className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
            <span>Happy for {practice?.manager || 'the practice manager'} to contact me about this</span>
          </label>
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)}
                   className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
            <span>Send this anonymously (we will not see your name — but we then cannot follow up)</span>
          </label>
        </div>

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-2xl bg-teal-600 py-4 text-base font-semibold text-white transition hover:bg-teal-700 disabled:opacity-50"
        >
          {busy ? 'Sending…' : 'Send privately'}
        </button>
      </form>
    </Shell>
  )
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto w-full max-w-lg rounded-3xl bg-white p-6 shadow-sm sm:p-8">
        {children}
        <p className="mt-8 border-t border-slate-100 pt-4 text-center text-[11px] leading-relaxed text-slate-400">
          Dr Chalita le Roux Inc · Roodepoort<br />
          Your feedback is stored securely and used only to improve the care we give you.
        </p>
      </div>
    </div>
  )
}
